/**
 * Buscador ⌘K de la v1.0.
 *
 * No es un extra: en el diseño original **es la única vía a Biblioteca**, que no
 * está en la barra, ni en el riel, ni en las tarjetas del panel. Sin el buscador,
 * esa vista queda inalcanzable — y ése sí sería un defecto de navegación.
 *
 * Las palabras clave salen del índice de su export (`kw:'logos tipografia paleta fotos'`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ROUTES } from '../lib/routes.ts';
import { navigate } from '../lib/router.ts';

/** Sinónimos por vista — lo que alguien escribiría, no cómo se llama el módulo. */
const CLAVES: Record<string, string> = {
  dashboard:     'panel inicio estado resumen kpi metricas',
  playground:    'simulador probar ruta demo',
  brief:         'pedido nuevo empezar necesito encargo',
  pipeline:      'produccion etapas progreso trabajo',
  casos:         'ejemplos reales tipos recorridos',
  aprobaciones:  'revisar aprobar rechazar decision humana',
  decision:      'motor reglas criterio como decide',
  arquitectura:  'capas sistema como esta construido',
  integraciones: 'herramientas conectadas estado plataformas',
  manual:        'documentacion convenciones control humano',
  faq:           'preguntas frecuentes dudas ayuda',
  biblioteca:    'logos tipografia paleta fotos marca activos',
};

const normalizar = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function CommandPalette({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const resultados = useMemo(() => {
    const t = normalizar(q.trim());
    if (!t) return ROUTES;
    return ROUTES.filter((r) =>
      normalizar(r.label + ' ' + r.title + ' ' + (CLAVES[r.id] ?? '')).includes(t));
  }, [q]);

  useEffect(() => { if (abierto) { setQ(''); setSel(0); inputRef.current?.focus(); } }, [abierto]);
  useEffect(() => { setSel(0); }, [q]);

  if (!abierto) return null;

  function elegir(i: number) {
    const r = resultados[i];
    if (r) { navigate(r.path); onCerrar(); }
  }

  function teclas(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onCerrar(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, resultados.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); elegir(sel); }
  }

  return (
    <div className="palette__scrim" onClick={onCerrar} role="presentation">
      <div
        className="palette" role="dialog" aria-modal="true" aria-label="Buscar en el sistema"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="palette__input"
          placeholder="Buscar una sección…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={teclas}
        />
        {resultados.length === 0 ? (
          <p className="palette__vacio">Sin coincidencias. La búsqueda no inventa resultados.</p>
        ) : (
          <ul className="palette__list">
            {resultados.map((r, i) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={'palette__item' + (i === sel ? ' palette__item--sel' : '')}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => elegir(i)}
                >
                  <span className="palette__label">{r.label}</span>
                  <span className="palette__hint">{r.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Registra ⌘K / Ctrl+K a nivel documento. */
export function useAtajoBuscar(abrir: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); abrir(); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [abrir]);
}
